/**
 * Create Role Template - Platform admin form for creating new role templates
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FormInput, FormTextArea, FormMultiSelect, FormSelect, Section, Card, Grid, Col, Badge } from '../../components';
import { AdminPageLayout } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import './CreateRoleTemplate.css';

const CreateRoleTemplate = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    is_staff: true
  });
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissions = await apiService.getAvailablePermissions();
        setAvailablePermissions(permissions.permissions || []);
      } catch (err) {
        showError('Failed to load available permissions');
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [showError]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.description.trim() || formData.permissions.length === 0) {
      showError('Please fill in all required fields and select at least one permission');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.createRoleTemplate(formData);
      showSuccess('Role template created successfully');
      navigate('/admin/role-templates');
    } catch (err) {
      showError(`Failed to create role template: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="PROVISION ROLE ARCHITECTURE" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Synchronizing available permissions...</div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="PROVISION ROLE ARCHITECTURE"
      subtitle="Define a new standardized permission set for ecosystem participants"
      backPath="/admin/role-templates"
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Governance Metadata">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormInput
                  label="Role Designation *"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Strategic Operational Director"
                  required
                />

                <FormTextArea
                  label="Scope of Authority *"
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Document the high-level responsibilities and governance scope of this role..."
                  rows={4}
                  required
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1.25rem', background: 'var(--surface-lvl2)', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                  <div style={{ flex: 1 }}>
                    <FormSelect
                      label="Classification Level"
                      name="is_staff"
                      value={formData.is_staff ? 'staff' : 'user'}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_staff: e.target.value === 'staff' }))}
                      options={[
                        { value: 'staff', label: 'Ecosystem Staff / Authorized Entity' },
                        { value: 'user', label: 'Standard Platform Participant' }
                      ]}
                      help="Authorized entities include coaches, directors, and administrators. Participants are typically parents and players."
                    />
                  </div>
                  {formData.is_staff ? (
                    <Badge variant="primary" size="sm">STAFF ROLE</Badge>
                  ) : (
                    <Badge variant="outline" size="sm">MEMBER ROLE</Badge>
                  )}
                </div>
              </div>
            </Card>
          </Section>

          <Section title="Permissions">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <FormMultiSelect
                  label="Authorized Capabilities *"
                  name="permissions"
                  value={formData.permissions}
                  onChange={(e) => setFormData(prev => ({ ...prev, permissions: e.target.value || [] }))}
                  options={availablePermissions.map(perm => ({
                    value: perm.id,
                    label: `${perm.name} — ${perm.description}`,
                    group: perm.category
                  }))}
                  placeholder="Select governance patterns for this role architecture"
                  required
                />
              </div>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1rem',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/role-templates')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={isSubmitting}
              disabled={isSubmitting || !formData.name.trim() || !formData.description.trim() || formData.permissions.length === 0}
            >
              Initialise Role Pattern
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default CreateRoleTemplate;